import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Zubehör-Karussell im Cart Drawer (snippets/cart-upsell.liquid).
 * Variantenwahl aktualisiert Preis, Streichpreis, Badge und Bild der Karte.
 * Der Plus-Button legt die gewählte Variante per /cart/add.js in den Warenkorb, zeigt kurz einen Haken
 * und meldet die Änderung danach dem Theme genauso wie die Geschenk-Automatik (cart-rewards.js):
 * per /cart/update.js ohne Änderung, damit Horizon den kompletten Warenkorb plus Sektions-HTML bekommt
 * und den Drawer sofort neu zeichnet. /cart/add.js allein liefert nur die Position, nicht den Warenkorb.
 */

class CartUpsellItem extends HTMLElement {
  static SECTION = 'cart-drawer-section';
  #busy = false;

  connectedCallback() {
    this.select = this.querySelector('[data-upsell-select]');
    this.button = this.querySelector('[data-upsell-add]');
    this.select?.addEventListener('change', this.#onChange);
    this.button?.addEventListener('click', this.#onAdd);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#onChange);
    this.button?.removeEventListener('click', this.#onAdd);
  }

  get #routes() {
    const r = window.Theme?.routes || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      update: r.cart_update_url || '/cart/update.js',
    };
  }

  async #post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || `${url} → ${res.status}`);
    return data;
  }

  #onChange = () => {
    const option = this.select?.selectedOptions?.[0];
    if (!option || !this.button) return;
    this.button.dataset.variantId = option.value;
    this.button.disabled = option.disabled;

    const price = this.querySelector('[data-upsell-price]');
    const compare = this.querySelector('[data-upsell-compare]');
    const badge = this.querySelector('[data-upsell-badge]');
    const image = this.querySelector('[data-upsell-image]');

    if (price && option.dataset.price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
    if (badge) {
      badge.hidden = !option.dataset.pct;
      if (option.dataset.pct) badge.textContent = `AKTION −${option.dataset.pct}%`;
    }
    if (image && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }
  };

  #onAdd = async (event) => {
    event.preventDefault();
    if (this.#busy || !this.button) return;
    const id = Number(this.button.dataset.variantId);
    if (!id) return;

    this.#busy = true;
    this.button.disabled = true;

    try {
      // 1. Position in den Warenkorb legen
      await this.#post(this.#routes.add, { items: [{ id, quantity: 1 }] });

      // 2. Haken zeigen, ohne zu warten: das Neuzeichnen läuft parallel
      this.button.classList.add('is-done');

      // 3. Theme informieren: Warenkorb + Sektions-HTML holen und ins Standard-Event geben
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [],
          promise: deferred.promise,
        })
      );
      try {
        const response = await this.#post(this.#routes.update, {
          updates: {},
          sections: CartUpsellItem.SECTION,
          sections_url: window.location.pathname,
        });
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
          detail: {
            sections: response.sections,
            items: response.items,
            itemCount: response.item_count,
            source: 'cart-upsell',
            didError: false,
          },
        });
      } catch (error) {
        deferred.reject(error);
        throw error;
      }
    } catch (error) {
      console.warn('[cart-upsell] Hinzufügen fehlgeschlagen', error);
    } finally {
      this.#busy = false;
      // Falls die Karte nach dem Neuzeichnen noch da ist: Haken zurück auf Warenkorb-Symbol
      if (this.isConnected && this.button) {
        setTimeout(() => {
          this.button.classList.remove('is-done');
          this.button.disabled = false;
        }, 1200);
      }
    }
  };
}

if (!customElements.get('cart-upsell-item')) {
  customElements.define('cart-upsell-item', CartUpsellItem);
}
